/* eslint-disable */
import React, { useState, useEffect } from 'react';
import { Field, FieldArray, ErrorMessage } from 'formik';
import Modal from 'react-responsive-modal';
import DropdownFieldComponent from '../common/FormComponents/DropdownFieldComponent';
import InputFieldComponent from '../common/FormComponents/InputFieldComponent';
import PoAttachments from './PoAttachments';
import polygon from '../../images/icons/polygon.svg';
import { Button } from '@synergy-marine-group/synergy-ui';
import showAlert from '../../utils/alert';
const emptyAdvanceItem = () => ({
  id: null,
  purchaseOrderId: null,
  arqPoNumber: null,
  paymentTermsId: '',
  advanceAmount: '',
  proformaInvoiceNo: '',
  proformaInvoiceDate: null,
  remarks: '',
  status: null,
  arqSeqNo: 0,
  arqAttachmentIds: [],
  arqAttachments: [],
});

// Normalize attachments for PoAttachments: keep preSignedUrl/preSignedURL and preview so open in new tab works
const normalizeAttachmentsForDisplay = (row = {}) => {
  const raw = row?.arqAttachments ?? row?.advanceAttachments ?? row?.arqAttachmentIds ?? [];
  const list = Array.isArray(raw) ? raw : [];
  return list.map((att) => ({
    id: att?.id ?? att?.attachmentId,
    attachmentId: att?.attachmentId ?? att?.id,
    name: att?.attachmentName ?? att?.name ?? att?.fileName ?? '',
    size: att?.fileSize ?? att?.size ?? 0,
    filePath: att?.filePath,
    preSignedUrl: att?.preSignedUrl ?? att?.preSignedURL,
    preview: att?.preview,
  }));
};

// ARQ status rules: Created, Paid, Cancelled = locked (cannot edit fields). Empty status = can edit.
// Created rows may still use CANCEL to withdraw the ARQ (see cancelDisabled).
const ARQ_LOCKED_STATUSES = ['created', 'paid', 'cancelled'];
const normalizeStatus = (status) => {
  if (status == null) return '';
  if (typeof status === 'object') return String(status?.value ?? status?.label ?? '').trim().toLowerCase();
  return String(status).trim().toLowerCase();
};
const isArqStatusEmpty = (status) => normalizeStatus(status) === '';
const isArqItemLocked = (item) => {
  const status = item?.status;
  if (isArqStatusEmpty(status)) return false;
  const s = normalizeStatus(status);
  return ARQ_LOCKED_STATUSES.includes(s);
};
const isArqStatusPaid = (item) => normalizeStatus(item?.status) === 'paid';
const isArqStatusCancelled = (item) => normalizeStatus(item?.status) === 'cancelled';
const isArqStatusCreated = (item) => normalizeStatus(item?.status) === 'created';
const isArqStatusNonEmpty = (status) => !isArqStatusEmpty(status) && ARQ_LOCKED_STATUSES.includes(normalizeStatus(status));
const hasAnyArqWithEmptyStatus = (items) => (items || []).some((item) => isArqStatusEmpty(item?.status));

const hasArqNumber = (item) => {
  const n = item?.arqNumber ?? item?.arqPoNumber ?? '';
  return typeof n === 'string' ? n.trim() !== '' : !!n;
};

/** ARQ row has been created/saved (issued number or status set) — toggle must stay fixed */
const hasAnyPersistedArqRow = (items) =>
  (items || []).some((item) => hasArqNumber(item) || !isArqStatusEmpty(item?.status));

const AdvanceRequest = ({
  values = {},
  setFieldValue,
  validateForm,
  errors = {},
  mode = 'add',
  disabled = false,
  paymentTermsOptions = [],
  poId,
  companyId,
  expenseCategoryId,
  agencyPOConfig = null,
  onCancelAdvancePo = null,
  isCancelAdvancePoLoading = false
}) => {
  const safeValues = values || {};
  const sectionExpandedStorageKey = `advanceRequest_sectionExpanded_${poId || 'global'}`;
  const companyIdVal = companyId ?? safeValues?.companyId;
  const expenseCategoryIdVal = expenseCategoryId ?? safeValues?.expenseCategoryId;
  const canCheckArq = companyIdVal != null && companyIdVal !== '' && expenseCategoryIdVal != null && expenseCategoryIdVal !== '';
  const isAdvanceRequestAvailable = agencyPOConfig?.isAdvanceRequestAvailable === true;

  const hasEditModeAdvanceData = poId && (safeValues?.isAdvancePO === true || (safeValues?.advancePurchaseOrder?.length ?? 0) > 0);

  // Header expand/collapse only (separate from toggle switch)
  const [sectionExpanded, setSectionExpanded] = useState(() => {
    if (hasEditModeAdvanceData) return true;
    try {
      if (typeof window !== 'undefined') {
        const saved = localStorage.getItem(sectionExpandedStorageKey);
        if (saved !== null) return saved === 'true';
      }
    } catch (e) {
      // ignore
    }
    return true;
  });

  const [cancelOrDeleteModal, setCancelOrDeleteModal] = useState({ open: false, index: null, item: null });

  useEffect(() => {
    if (typeof setFieldValue !== 'function' || values == null) return;
    const currentSafeValues = values || {};
    if (poId && (currentSafeValues.isAdvancePO === true || (currentSafeValues.advancePurchaseOrder?.length ?? 0) > 0)) {
      setSectionExpanded(true);
      return;
    }
    if (currentSafeValues.isAdvancePO === undefined) {
      try { setFieldValue('isAdvancePO', false); } catch (err) { /* Formik not ready */ }
    }
    if (currentSafeValues.advancePurchaseOrder == null) {
      try { setFieldValue('advancePurchaseOrder', []); } catch (err) { /* Formik not ready */ }
    }
  }, [poId, values?.isAdvancePO, values?.advancePurchaseOrder?.length]);

  const onHeaderClick = () => {
    const next = !sectionExpanded;
    setSectionExpanded(next);
    try {
      if (typeof window !== 'undefined') localStorage.setItem(sectionExpandedStorageKey, next ? 'true' : 'false');
    } catch (e) {
      // ignore
    }
  };

  const isAdvancePOOn = safeValues?.isAdvancePO === true;
  const advancePurchaseOrder = safeValues?.advancePurchaseOrder || [];

  const onToggleSwitch = () => {
    if (hasAnyPersistedArqRow(values?.advancePurchaseOrder)) return;
    if (disabled && !isAdvanceRequestAvailable) return;
    const next = !isAdvancePOOn;
    if (typeof setFieldValue === 'function' && values != null) {
      setFieldValue('isAdvancePO', next);
      if (next && (values?.advancePurchaseOrder?.length ?? 0) === 0) {
        setFieldValue('advancePurchaseOrder', [emptyAdvanceItem()]);
      }
    }
  };
  // When Advance Request is ON and there are no ARQ rows, auto-create one empty row so fields and attachment show immediately
  useEffect(() => {
    if (!isAdvancePOOn || typeof setFieldValue !== 'function' || values == null) return;
    const items = values?.advancePurchaseOrder;
    if (items == null) return;
    if (items.length > 0) return;
    setFieldValue('advancePurchaseOrder', [emptyAdvanceItem()]);
  }, [isAdvancePOOn, setFieldValue, values?.advancePurchaseOrder?.length]);

  const safePaymentTermsOptions = (paymentTermsOptions || []).map((o) => ({ label: o?.label ?? '', value: o?.value ?? '' }));

  // Add mode: enable when company+expense category selected and config says isAdvanceRequestAvailable.
  // Edit mode (poId): enable when getAgencyPOConfig says isAdvanceRequestAvailable so user can add ARQ in PO Edit if not added at creation.
  const isToggleDisabled =
    !isAdvanceRequestAvailable || (isAdvancePOOn && hasAnyPersistedArqRow(advancePurchaseOrder));
  const isSectionDisabled = !isAdvanceRequestAvailable ? disabled : false;
  const hasAdvanceRequestData = (safeValues?.advancePurchaseOrder?.length ?? 0) > 0;
  // Show form when: (toggle ON and not locked) OR (read-only view with data, e.g. PODetailPage)
   const showAdvanceRequestForm = isAdvancePOOn;

  return (
    <div className="my-4">
      <div
        onClick={onHeaderClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onHeaderClick(); }}
        aria-expanded={sectionExpanded}
        className="flex flex-row place-content-between mb-4  mt-4 bg-secondary-500 p-1 px-2.5 rounded-md  cursor-pointer"
      >
        <div className="text-base font-muli text-white-light font-bold">Advance Request</div>
        <img src={polygon} alt="expand/collapse" className={`${!sectionExpanded ? 'transform -rotate-90' : ''} w-6`} />
      </div>

      {sectionExpanded && (
        <>
          <div
            className="flex items-center gap-4 mb-4 mt-2"
            style={{ opacity: isToggleDisabled ? 0.3 : 1 }}
          >
            <label className="toggle-switch toggle-switch--purple" onClick={(e) => e.stopPropagation()}>
              <input type="checkbox" checked={!!isAdvancePOOn} onChange={onToggleSwitch} disabled={isToggleDisabled} />
              <span className="adslider" />
            </label>
            <div className="block text-sm font-muli text-gray-100 text-left">Advance Request</div>
          </div>
              {typeof errors?.advancePurchaseOrder === 'string' && (
                <div className="text-red-500 text-sm mt-1 block text-left" role="alert">
                  {errors.advancePurchaseOrder}
                </div>
              )}
          {showAdvanceRequestForm && (
        <>
        <FieldArray
          name="advancePurchaseOrder"
          render={({ push, remove }) => (
            <div className="p-4 rounded border" style={{ border: '2px solid rgb(238, 238, 238)', borderRadius: '10px' }}>
              {(safeValues?.advancePurchaseOrder || []).length > 0 ? (
                (safeValues.advancePurchaseOrder || []).map((item, index) => {
                  const isItemLocked = mode === 'edit' && isArqItemLocked(item);
                  const hasEmptyStatus = isArqStatusEmpty(item?.status);
                  const isRowDisabled = isItemLocked || ((disabled || isSectionDisabled) && !hasEmptyStatus);
                  return (
                  <div key={item.id || index} className="p-5 mb-4" style={{ border: '2px solid #EEEEEE', borderRadius: '10px', opacity: isItemLocked ? 0.85 : 1 }}>
                    {mode === 'edit' && (
                      <div className="mt-2 flex flex-row justify-between mb-4">
                        <div className="w-full md:w-1/4">
                          <span className="text-md"> Advance Request No :</span>
                          <span className="font-bold text-md">{item?.arqNumber || item?.arqPoNumber || '-'}</span>
                        </div>
                        <div className="w-full md:w-1/4">
                          <span className="text-md"> SAP Adv. Request No. :</span>
                          <span className="font-bold text-md">{item?.sapArqNumber || '-'}</span>
                        </div>
                        <div className="w-full md:w-1/4">
                          <span className="text-md"> Status:</span>
                          <span className="font-bold text-md">{item?.status || '-'}</span>
                          {/* {isItemLocked && (
                            <span className="block text-xs text-amber-600 mt-0.5">This ARQ cannot be edited.</span>
                          )} */}
                        </div>
                      </div>
                    )}
                    <div className="grid grid-cols-3 gap-6 p-1 mb-4">
                      <div className="required-field">
                        <Field
                          name={`advancePurchaseOrder[${index}].paymentTermsId`}
                          component={DropdownFieldComponent}
                          special={{ label: 'Select', value: '' }}
                          options={safePaymentTermsOptions}
                          className="w-full"
                          rounded
                          isDisabled={isRowDisabled}
                          label="Payment Terms"
                          requiredField
                        />
                        <ErrorMessage
                          name={`advancePurchaseOrder[${index}].paymentTermsId`}
                          component="div"
                          className="error text-xs mt-1"
                        />
                      </div>
                      <div className="required-field">
                        <Field
                          name={`advancePurchaseOrder[${index}].advanceAmount`}
                          component={InputFieldComponent}
                          className="w-full"
                          id={`advanceAmount-${index}`}
                          type="number"
                          rounded
                          disabled={isRowDisabled}
                          label="Advance Amount"
                          requiredField
                        />
                        <ErrorMessage
                          name={`advancePurchaseOrder[${index}].advanceAmount`}
                          component="div"
                          className="error text-xs mt-1"
                        />
                      </div>
                      <div className="required-field">
                        <Field
                          name={`advancePurchaseOrder[${index}].proformaInvoiceNo`}
                          component={InputFieldComponent}
                          className="w-full"
                          id={`proformaInvoiceNo-${index}`}
                          type="text"
                          rounded
                          disabled={isRowDisabled}
                          label="Proforma Invoice No."
                          requiredField
                        />
                        <ErrorMessage
                          name={`advancePurchaseOrder[${index}].proformaInvoiceNo`}
                          component="div"
                          className="error text-xs mt-1"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-6 p-1 mb-4">
                      <Field
                        name={`advancePurchaseOrder[${index}].proformaInvoiceDate`}
                        component={InputFieldComponent}
                        className="w-full"
                        id={`proformaInvoiceDate-${index}`}
                        type="date"
                        rounded
                        disabled={isRowDisabled}
                        label="Proforma Invoice Date"
                      />
                    </div>

                    <div className="col-span-2 mb-4 required-field">
                      <PoAttachments
                        name="arqAttachments"
                        attachmentType="arq"
                        poId={poId}
                        arqId={safeValues?.advancePurchaseOrder?.[index]?.id ?? ''}
                        values={{
                          ...(safeValues?.advancePurchaseOrder?.[index] || {}),
                          arqAttachments: normalizeAttachmentsForDisplay(safeValues?.advancePurchaseOrder?.[index]),
                        }}
                        setFieldValue={(_fieldName, value) => {
                          setFieldValue(`advancePurchaseOrder[${index}].arqAttachments`, value, true);
                          if (typeof validateForm === 'function') {
                            setTimeout(() => validateForm(), 0);
                          }
                        }}
                        disabled={isRowDisabled}
                        showHeader={false}
                        showPreview={false}
                        onUploadSuccess={(response) => {
                          const ids = response?.arqAttachmentIds ?? response?.poAttachmentIds ?? [];
                          if (Array.isArray(ids) && ids.length > 0) {
                            const existing = safeValues?.advancePurchaseOrder?.[index]?.arqAttachmentIds ?? [];
                            setFieldValue(`advancePurchaseOrder[${index}].arqAttachmentIds`, [...existing, ...ids]);
                          }
                        }}
                      />
                      <div className="block mt-1 min-h-[1.25rem]" aria-live="polite">
                        <ErrorMessage
                          name={`advancePurchaseOrder.${index}.arqAttachments`}
                          component="div"
                          className="error text-xs -mt-3.5"
                        />
                      </div>
                    </div>

                    <Field
                      name={`advancePurchaseOrder[${index}].remarks`}
                      component={InputFieldComponent}
                      className="w-full mb-4"
                      id={`remarks-${index}`}
                      type="text"
                      rounded
                      multiline
                      rows={2}
                      disabled={isRowDisabled}
                      label="Remarks"
                    />

                    <div className="text-right pt-4">
                      {(() => {
                        const hasArq = hasArqNumber(item);
                        // Created ARQs: row stays read-only but user may cancel (withdraw) the advance request
                        const cancelDisabled = hasArq && (
                          (isRowDisabled && !isArqStatusCreated(item)) ||
                          isSectionDisabled ||
                          isArqStatusPaid(item) ||
                          isArqStatusCancelled(item)
                        );
                        const deleteDisabled = !hasArq && (
                          isRowDisabled ||
                          isSectionDisabled
                        );
                        const isCancelDeleteDisabled = isCancelAdvancePoLoading || (hasArq ? cancelDisabled : deleteDisabled);
                        const buttonLabel = hasArq
                          ? (isArqStatusCancelled(item) ? 'Cancelled' : (isCancelAdvancePoLoading ? 'Cancelling...' : 'CANCEL'))
                          : (isCancelAdvancePoLoading ? 'Deleting...' : 'DELETE');
                        return (
                          <Button
                            type="button"
                            className={`rounded-md uppercase tracking-wide font-montserrat font-semibold max-w-57 px-6
    border-gray-700 text-gray-700 h-10 text-sm hover:shadow-md focus:outline-none
    fill-btn active:text-white-light bg-white-dark ${isCancelDeleteDisabled ? 'pointer-events-none cursor-not-allowed !bg-gray-100 !text-gray-500  !border-0 !border-transparent !shadow-none' : ''}`}
                            style={isCancelDeleteDisabled ? {
                              backgroundColor: 'rgb(238 238 238)',
                              borderColor: 'transparent',
                              boxShadow: 'none',
                            } : undefined}
                            onClick={() => {
                              const rowItem = safeValues?.advancePurchaseOrder?.[index];
                              setCancelOrDeleteModal({ open: true, index, item: rowItem });
                            }}
                            size="large"
                            variant="outlined"
                            disabled={isCancelDeleteDisabled}
                          >
                            {buttonLabel}
                          </Button>
                        );
                      })()}
                    </div>
                  </div>
                ); })
              ) : null}

              <div className="pt-2 pb-2">
                <Button
                  type="button"
                  className="rounded-md w-full w-100 max-w-full bg-amber-100"
                  onClick={() => push(emptyAdvanceItem())}
                  disabled={isSectionDisabled || mode === 'add' || hasAnyArqWithEmptyStatus(safeValues?.advancePurchaseOrder)}
                  size="large"
                  variant="filled"
                >
                  ADD ARQ
                </Button>
              </div>
            </div>
          )}
        />
        </>
          )}
        </>
      )}

      <Modal
        open={cancelOrDeleteModal.open}
        onClose={() => setCancelOrDeleteModal({ open: false, index: null, item: null })}
        center
        showCloseIcon
        styles={{ modal: { width: '450px' } }}
      >
        <div className="p-4">
          {cancelOrDeleteModal.item && hasArqNumber(cancelOrDeleteModal.item) ? (
            <>
              <div className="text-lg font-semibold mb-3" style={{ color: 'rgba(248, 177, 19, 1)' }}>
                Cancel Advance Request
              </div>
              <p className="text-gray-100 mb-6">
                Advance Request No. <strong>{cancelOrDeleteModal.item?.arqNumber || cancelOrDeleteModal.item?.arqPoNumber || '-'}</strong> will be set to Cancelled. Continue?
              </p>
              <div className="flex justify-end gap-3">
                <Button
                  type="button"
                  variant="outlined"
                  size="medium"
                  rounded
                  onClick={() => setCancelOrDeleteModal({ open: false, index: null, item: null })}
                >
                  Close
                </Button>
                <Button
                  type="button"
                  variant="filled"
                  size="medium"
                  rounded
                  onClick={() => {
                    const { index } = cancelOrDeleteModal;
                    setFieldValue(`advancePurchaseOrder[${index}].status`, 'Cancelled');
                    showAlert('Advance request will be set to Cancelled. Send PO to submit the change.', 'success');
                    setCancelOrDeleteModal({ open: false, index: null, item: null });
                  }}
                >
                  Cancel
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="text-lg font-semibold mb-3" style={{ color: 'rgba(248, 177, 19, 1)' }}>
                Delete Advance Request row
              </div>
              <p className="text-gray-100 mb-6">
                This row has not been saved. It will be removed from the form. Continue?
              </p>
              <div className="flex justify-end gap-3">
                <Button
                  type="button"
                  variant="outlined"
                  size="medium"
                  rounded
                  onClick={() => setCancelOrDeleteModal({ open: false, index: null, item: null })}
                >
                  Close
                </Button>
                <Button
                  type="button"
                  variant="filled"
                  size="medium"
                  rounded
                  onClick={() => {
                    const { index } = cancelOrDeleteModal;
                    const current = safeValues?.advancePurchaseOrder || [];
                    const next = current.filter((_, i) => i !== index);
                    setFieldValue('advancePurchaseOrder', next);
                    if (next.length === 0 && mode === 'add') {
                      setFieldValue('isAdvancePO', false);
                    }
                    setCancelOrDeleteModal({ open: false, index: null, item: null });
                  }}
                >
                  Delete
                </Button>
              </div>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default AdvanceRequest;
